import { useEffect, useState } from "react";
import { AdminService } from "../../../Services/Admin.service";
import ConfirmationModal from "../../../Components/ConfirmationModal";
import Toaster from "../../../Components/Toaster";
import { getErrorMessagesList } from "../../../Utils/Common";
import Loader from "../../../Components/Loader";

function PaymentPreferences({ donorId, userData }) {

   const [paymentPreferences, setPaymentPreferences] = useState([]);
   const [amounts, setAmounts] = useState({});
   const [errors, setErrors] = useState({});
   const [loading, setLoading] = useState(false);
   const [selectedMethod, setSelectedMethod] = useState(null);

   // Toaster
   const [showToast, setShowToast] = useState(false);
   const [toastMessage, setToastMessage] = useState([]);
   const [toastVariant, setToastVariant] = useState("");

   // Confirm modal
   const [showConfirmModal, setShowConfirmModal] = useState(false);

   useEffect(() => {
      fetchPaymentPreference();
   }, []);

   const fetchPaymentPreference = async () => {
      const result = await AdminService.getDonorPaymentMethodList(donorId);
      if (result.status && result.data.length) {
         setPaymentPreferences(result.data);
      }
   };

   const formatName = (name) =>
      name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

   const getStatusLabel = (status) =>
      status === "1"
         ? <span className="text-success">Active</span>
         : <span className="text-danger">Pending</span>;

   const handleConfirm = (pref) => {
      if (!amounts[pref.id]) {
         setErrors(prev => ({ ...prev, [pref.id]: "Please Enter Payment Amount." }));
         return;
      }
      setSelectedMethod(pref);
      setShowConfirmModal(true);
   };

   const confirmPayment = async () => {
      if (!selectedMethod) return;
      setLoading(true);

      const amount = parseFloat(amounts[selectedMethod.id] || "0");
      setShowConfirmModal(false);
      
      try {
         const result = await AdminService.verifyUserPaymentMethod(
            donorId,
            selectedMethod.id,
            amount
         );

         setToastMessage(getErrorMessagesList(result));
         setToastVariant(result.status ? "success" : "error");
         setShowToast(true);

      } catch (err) {
         const messages = getErrorMessagesList(err);
         setToastMessage(messages);
         setToastVariant("error");
         setShowToast(true);

      } finally {
         setLoading(false);
         fetchPaymentPreference();
      }
   };

   return (
      <>
         <Loader loading={loading} /> 

         <h3>Payment Preferences</h3>

         {paymentPreferences.length === 0 && (
            <div className="text-danger mt-2">Not Set</div>
         )}

         {paymentPreferences.map((p, index) => (
            <div key={index} className="mt-3">

               {/* Header */}
               <div className="d-flex flex-wrap gap-2 align-items-center mb-3">

                  <span><strong>Payment Method:</strong> {formatName(p.payment_method_name)}</span>
                  <span className="mx-2">|</span>

                  <span><strong>Username:</strong> {p.payment_username}</span>
                  <span className="mx-2">|</span>

                  <span><strong>Verification:</strong> {p.account_status === "1"
                     ? <span className="text-success">Verified</span>
                     : <span className="text-danger">Pending</span>}</span>
                  <span className="mx-2">|</span>

                  <span><strong>Payment Method Status:</strong> {getStatusLabel(p.payment_method_status)}</span>

               </div>

               {/* Notification already sent */}
               {p.is_notification_sent === "1" &&  (
                  <>
                     <p className="payment-preference-notification-sent">(Notification sent)</p>
                     {index !== paymentPreferences.length - 1 && <hr />}
                  </>
               )}

               {/* Enter amount */}
               {p.is_notification_sent === "0" && (
                  <div>
                     <div className="mb-3">
                        <label className="form-label">
                           Payment Sent Amount <span className="text-danger">*</span>
                        </label>

                        <input
                           type="text"
                           inputMode="decimal"
                           className="form-control"
                           placeholder="Enter amount"
                           value={amounts[p.id] || ""}
                           onChange={(e) => {
                              let value = e.target.value;

                              if (value === "." || value.startsWith(".")) {
                                 setErrors(prev => ({ ...prev, [p.id]: "Please Enter Valid Payment Amount." }));
                                 return;
                              }

                              if (!/^\d*\.?\d*$/.test(value)) return;

                              if (value.endsWith(".")) {
                                 setErrors(prev => ({ ...prev, [p.id]: "Please Enter Valid Payment Amount." }));
                                 setAmounts(prev => ({ ...prev, [p.id]: value }));
                                 return;
                              }

                              if (value.length > 4) {
                                 setErrors(prev => ({ ...prev, [p.id]: "Please enter not more than 4 characters." }));
                                 return;
                              }

                              setErrors(prev => ({ ...prev, [p.id]: "" }));
                              setAmounts(prev => ({ ...prev, [p.id]: value }));
                           }}
                        />

                        {errors[p.id] && (
                           <p className="text-danger mt-1">{errors[p.id]}</p>
                        )}
                     </div>

                     <button className="el-main-button"
                        onClick={() => handleConfirm(p)}>
                        <span>Confirm & Send Notification</span>
                     </button>
                  </div>
               )}
            </div>
         ))}

         {/* Confirmation Modal */}
         <ConfirmationModal
            show={showConfirmModal}
            onHide={() => setShowConfirmModal(false)}
            onConfirm={confirmPayment}
            title="Confirm Payment"
            body={`Are you sure you want to make this payment of ${amounts[selectedMethod?.id]} to ${userData?.username}?`}
            type="deactivateactivate"
         />

         {/* Toaster */}
         <Toaster
            show={showToast}
            message={toastMessage}
            variant={toastVariant}
            onClose={() => setShowToast(false)}
         />
      </>
   );
}

export default PaymentPreferences;
